<script setup lang="ts">
import { computed, ref } from 'vue';
import { useMutation, useQueryClient } from '@tanstack/vue-query';
import { medicationsApi, type MedicationOrder } from '@/api/medications';
import { apiErrorText } from '@/utils/api-errors';
import ResponsiveDialog from '@/components/responsive-dialog.vue';

export type OrderAction = 'stop' | 'void' | 'hold' | 'holdAll';

// Stop (now or on a later date), void a transcription mistake, or pause one
// order or every order of the patient.
const props = defineProps<{ order: MedicationOrder; action: OrderAction }>();
const emit = defineEmits<{ close: [] }>();

const queryClient = useQueryClient();
const reason = ref('');
const when = ref<'now' | 'later'>('now');
const at = ref('');
const until = ref('');
const error = ref('');

const COPY = {
  stop: {
    title: 'Suspender indicación', button: 'Suspender',
    text: 'Deja de contar desde ahora o desde la fecha que indiques. Queda en el historial.',
  },
  void: {
    title: 'Anular por error', button: 'Anular',
    text: 'Para una indicación mal transcrita: sale del plan y queda en el historial como anulada.',
  },
  hold: {
    title: 'Pausar indicación', button: 'Pausar',
    text: 'Mientras esté en pausa no hay dosis pendientes. Se reanuda manualmente o en la fecha indicada.',
  },
  holdAll: {
    title: 'Pausar todas las indicaciones', button: 'Pausar todas',
    text: 'Pausa todas las indicaciones vigentes y próximas del paciente, por ejemplo durante una hospitalización.',
  },
} as const;
const copy = computed(() => COPY[props.action]);
const isHold = computed(() => props.action === 'hold' || props.action === 'holdAll');
const valid = computed(() => (
  (props.action !== 'void' || reason.value.trim().length > 0) &&
  (props.action !== 'stop' || when.value === 'now' || !!at.value)
));

const save = useMutation({
  mutationFn: () => {
    const id = props.order.id;
    if (props.action === 'stop') {
      return medicationsApi.stop(id, { reason: reason.value, at: when.value === 'later' ? at.value : undefined });
    }
    if (props.action === 'void') return medicationsApi.void(id, reason.value);

    return medicationsApi.hold(id, {
      all: props.action === 'holdAll', reason: reason.value, to: until.value || undefined,
    });
  },
  onSuccess: () => {
    queryClient.invalidateQueries({ queryKey: ['medicationOrders', props.order.patientId] });
    emit('close');
  },
  onError: (failure) => {
    error.value = apiErrorText(failure, 'No se pudo guardar.');
  },
});
</script>
<template>
  <responsive-dialog
    :model-value="true"
    :title="copy.title"
    max-width="520"
    @close="emit('close')"
  >
    <div class="d-flex flex-column ga-4 pt-2">
      <p class="text-body-2">
        <b>{{ order.drugName }}</b>
        <span class="d-block text-medium-emphasis">{{ copy.text }}</span>
      </p>
      <template v-if="action === 'stop'">
        <v-radio-group
          v-model="when"
          density="compact"
          hide-details
        >
          <v-radio
            label="Desde ahora"
            value="now"
          />
          <v-radio
            label="Desde una fecha y hora"
            value="later"
          />
        </v-radio-group>
        <v-text-field
          v-if="when === 'later'"
          v-model="at"
          type="datetime-local"
          label="Deja de contar desde"
          variant="outlined"
          density="compact"
          hide-details
        />
      </template>
      <v-text-field
        v-if="isHold"
        v-model="until"
        type="datetime-local"
        label="Reanudar automáticamente (opcional)"
        variant="outlined"
        density="compact"
        hide-details
      />
      <v-text-field
        v-model="reason"
        :label="action === 'void' ? 'Motivo' : 'Motivo (opcional)'"
        variant="outlined"
        density="compact"
        hide-details
      />
      <v-alert
        v-if="error"
        type="error"
        variant="tonal"
        :text="error"
      />
    </div>
    <template #actions>
      <v-spacer />
      <v-btn
        :disabled="save.isPending.value"
        @click="emit('close')"
      >
        Cancelar
      </v-btn>
      <v-btn
        :color="action === 'void' ? 'error' : 'primary'"
        variant="flat"
        :disabled="!valid"
        :loading="save.isPending.value"
        @click="save.mutate()"
      >
        {{ copy.button }}
      </v-btn>
    </template>
  </responsive-dialog>
</template>
